import { inject, Injectable } from '@angular/core';
import { ActiveCartService } from '@cart/data-access';
import { CheckoutActions, CheckoutApiService } from '@checkout-v2/data-access';
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DeliveryAddressService, ReceiptService } from '../services';

@Injectable()
export class SavedDeliveryAddressEffects implements OnRunEffects {
  private readonly actions$ = inject(Actions);
  private readonly checkoutApiService = inject(CheckoutApiService);
  private readonly activeCartService = inject(ActiveCartService);
  private readonly deliveryAddressService = inject(DeliveryAddressService);
  private readonly receiptTypeService = inject(ReceiptService);

  // Загрузить список адресов доставки
  // Отправить запрос на бекенд
  loadAddressList$: Observable<
    CheckoutActions.LoadSavedDeliveryAddressListSuccess | CheckoutActions.LoadSavedDeliveryAddressListFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_SAVED_DELIVERY_ADDRESS_LIST),
      concatLatestFrom(() => this.activeCartService.getActiveCartId()),
      filter(([, cartId]) => !!cartId),
      switchMap(([, cartId]) =>
        this.checkoutApiService.loadDeliveryAddressList(cartId).pipe(
          map((res) => new CheckoutActions.LoadSavedDeliveryAddressListSuccess(res)),
          catchError((error) => of(new CheckoutActions.LoadSavedDeliveryAddressListFail(error))),
        ),
      ),
    ),
  );

  // Выбран способ получения "доставка"
  // Загрузить список адресов доставки
  loadAddressListAfterSetReceiptType$: Observable<CheckoutActions.LoadSavedDeliveryAddressList> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadReceiptTypeSuccess>(CheckoutActions.LOAD_RECEIPT_TYPE_SUCCESS),
      switchMap(() => this.receiptTypeService.getActiveReceiptTypeId()),
      filter((activeReceiptType) => activeReceiptType === 'delivery'),
      map(() => new CheckoutActions.LoadSavedDeliveryAddressList()),
    ),
  );

  // Выбран способ получения не равный "доставка"
  // Сбросить список адресов доставки
  resetAddressListAfterSetReceiptType$: Observable<CheckoutActions.LoadSavedDeliveryAddressListReset> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CheckoutActions.LoadReceiptTypeSuccess>(CheckoutActions.LOAD_RECEIPT_TYPE_SUCCESS),
        switchMap(() => this.receiptTypeService.getActiveReceiptTypeId()),
        filter((activeReceiptType) => activeReceiptType !== 'delivery'),
        concatLatestFrom(() => this.deliveryAddressService.getAddressListState()),
        filter(([, state]) => !!state.error || !!state.success),
        map(() => new CheckoutActions.LoadSavedDeliveryAddressListReset()),
      ),
  );

  // -----------------------------------------

  // Сохранить адрес доставки
  saveAddress$: Observable<CheckoutActions.LoadSavedDeliveryAddressList> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.SaveDeliveryAddress>(CheckoutActions.SAVE_DELIVERY_ADDRESS),
      map((action) => action.payload),
      concatLatestFrom(() => this.activeCartService.getActiveCartId()),
      filter(([, cartId]) => !!cartId),
      switchMap(([address, cartId]) => {
        return this.checkoutApiService.saveDeliveryAddress(cartId, address).pipe(
          tap((res) => this.deliveryAddressService.selectActiveAddressId(res.id)),
          map(() => new CheckoutActions.LoadSavedDeliveryAddressList()),
        );
      }),
    ),
  );

  // Отредактировать сохраненный адрес доставки
  editAddress$: Observable<CheckoutActions.LoadSavedDeliveryAddressList> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.EditSavedDeliveryAddress>(CheckoutActions.EDIT_SAVED_DELIVERY_ADDRESS),
      map((action) => action.payload),
      concatLatestFrom(() => this.activeCartService.getActiveCartId()),
      filter(([, cartId]) => !!cartId),
      switchMap(([address, cartId]) =>
        this.checkoutApiService.editDeliveryAddress(cartId, address).pipe(
          tap(() => this.deliveryAddressService.selectActiveAddressId(address.id)),
          map(() => new CheckoutActions.LoadSavedDeliveryAddressList()),
        ),
      ),
    ),
  );

  // Удалить сохраненный адрес доставки
  deleteAddress$: Observable<CheckoutActions.LoadSavedDeliveryAddressList> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.DeleteSavedDeliveryAddress>(CheckoutActions.DELETE_SAVED_DELIVERY_ADDRESS),
      map((action) => action.payload),
      concatLatestFrom(() => this.activeCartService.getActiveCartId()),
      filter(([, cartId]) => !!cartId),
      switchMap(([addressId, cartId]) =>
        this.checkoutApiService
          .deleteDeliveryAddress(cartId, addressId)
          .pipe(map(() => new CheckoutActions.LoadSavedDeliveryAddressList())),
      ),
    ),
  );

  // -----------------------------------------

  // Загружен список адресов доставки
  // Установить или сбросить активный адрес доставки
  setActiveAddressAfterLoad$: Observable<CheckoutActions.SelectActiveDeliveryAddress> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadSavedDeliveryAddressListSuccess>(
        CheckoutActions.LOAD_SAVED_DELIVERY_ADDRESS_LIST_SUCCESS,
      ),
      map((action) => action.payload),
      concatLatestFrom(() => this.deliveryAddressService.activeAddressId$),
      map(([addressList, activeAddressId]) => {
        const current: string | undefined = activeAddressId;
        let next: string | undefined;

        if (addressList.length > 0) {
          next = (addressList.find((addr) => addr.id === activeAddressId) ?? addressList[0]).id;
        }

        return [current, next];
      }),
      filter(([current, next]) => current !== next),
      map(([, next]) => new CheckoutActions.SelectActiveDeliveryAddress(next)),
    ),
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return this.actions$.pipe(
      ofType(CheckoutActions.CHECKOUT_INIT),
      exhaustMap(() => resolvedEffects$.pipe(takeUntil(this.actions$.pipe(ofType(CheckoutActions.CHECKOUT_DESTROY))))),
    );
  }
}
